import axios from "axios";
import { shouldBlockRequest } from "@/helpers/handlers/demoService";
import { APP_DEMO } from "@/config/demo";
import { LOCAL_STORAGE_KEYS } from "@/enums/local-storage";

import { store } from "@/store/store";
import { i18n } from "@/main";
import { endpoints } from "@/config/endpoints";
import { ensureCurrentSession, getSessionId } from "@/helpers/auth-session";

export const axiosInstance = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  headers: {
    Accept: "application/json"
  }
});

const CancelToken = axios.CancelToken;

axiosInstance.interceptors.request.use(
  config => {
    config.sessionId = getSessionId();
    // if demo version, we cancel the request and show demo modal
    if (shouldBlockRequest(config)) {
      return {
        ...config,
        cancelToken: new CancelToken(cancel =>
          cancel(APP_DEMO.REQUEST_CANCEL_MSG)
        )
      };
    }

    // Logout carries the captured token of the session being closed. Local state
    // is already cleared, and a subsequent login must not change this header.
    if (config.url === endpoints.users.logout) {
      return config;
    }

    const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));
    const storageCompanyId = localStorage.getItem(
      LOCAL_STORAGE_KEYS.COMPANY_ID
    );
    const companyId =
      storageCompanyId ||
      store.getters["company/companyId"] ||
      store.state.company.id;
    const selectedLocale = store.getters["settings/selectedLocale"];

    if (user && user.token) {
      config.headers["Authorization"] = `Token ${user.token}`;
    } else {
      delete config.headers["Authorization"];
    }

    if (companyId) {
      config.headers["Company-Id"] = companyId;
    } else {
      delete config.headers["Company-Id"];
    }

    if (selectedLocale) {
      config.headers["Accept-Language"] = selectedLocale;
    } else {
      config.headers["Accept-Language"] = i18n.fallbackLocale; // by default: en-US
    }

    return config;
  },
  undefined,
  { synchronous: true }
);

const checkResponseSession = config => {
  if (config && config.url !== endpoints.users.logout) {
    ensureCurrentSession(config.sessionId);
  }
};

axiosInstance.interceptors.response.use(
  response => {
    checkResponseSession(response.config);
    return response;
  },
  error => {
    // In particular, a late 401 from X must never log out the new user Y.
    checkResponseSession(error.config);
    return Promise.reject(error);
  }
);

export const apiService = axiosInstance;
